/*
 * Boardhintergrund (V04, V05): Farbe, Verlauf oder Bild hinter den Spalten – mit Abschwächung durch
 * eine Fläche in Hintergrundfarbe, damit Texte auf dem Board lesbar bleiben. Kein Parallax, keine
 * Bewegung, kein Weichzeichner; passende Bildgröße je Bildschirm.
 */
import type { CSSProperties } from 'react';
import { THEME_SURFACES, type ResolvedBranding } from '@kaba/shared';

const DIM = { calm: 0.72, balanced: 0.5, strong: 0.28 } as const;

function rgba(hex: string, a: number) {
  return `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, ${a})`;
}

/** Kleinste Variante, die die Breite füllt (auf schmalen Geräten nie die große Datei) */
export function pickVariant(urls: Record<string, string>, cssWidth: number, dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1): string | undefined {
  const need = cssWidth * Math.min(dpr, 2);
  const list = Object.entries(urls)
    .map(([k, u]) => [Number(k.slice(1)), u] as const)
    .sort((a, b) => a[0] - b[0]);
  return (list.find(([w]) => w >= need) ?? list[list.length - 1])?.[1];
}

export function backgroundStyle(
  bg: ResolvedBranding['background'] | undefined,
  mode: 'light' | 'dark',
  opts: { hideDecor?: boolean; width?: number } = {},
): CSSProperties | undefined {
  if (!bg || bg.mode === 'neutral') return undefined;
  const surface = THEME_SURFACES[mode].surface;
  // Im Dunkeln stärker zurücknehmen, damit helle Farben nicht blenden
  const extra = mode === 'dark' ? 0.25 : 0;
  const veil = (a: number) => `linear-gradient(${rgba(surface, Math.min(0.9, a + extra))}, ${rgba(surface, Math.min(0.9, a + extra))})`;
  if (bg.mode === 'solid' || (bg.mode === 'image' && (opts.hideDecor || !bg.urls))) {
    // Farbe wie gewählt; nur im Dunkeln gedämpft
    return { backgroundColor: bg.color, backgroundImage: mode === 'dark' ? veil(0.35) : undefined };
  }
  if (bg.mode === 'gradient') return { backgroundImage: `${mode === 'dark' ? `${veil(0.35)}, ` : ''}linear-gradient(135deg, ${bg.from}, ${bg.to})` };
  if (bg.mode === 'image') {
    const url = pickVariant(bg.urls!, opts.width ?? (typeof window !== 'undefined' ? window.innerWidth : 1600));
    return {
      backgroundColor: bg.color,
      backgroundImage: `${veil(DIM[bg.dim])}, url("${url}")`,
      backgroundSize: 'cover',
      backgroundPosition: `${bg.focusX}% ${bg.focusY}%`,
      backgroundRepeat: 'no-repeat',
    };
  }
  return undefined;
}
